import type { ProseMirrorNode } from './answer.js'
import type { DocumentVerwijzing } from './answer.js'
import { withoutForbiddenXmlCharacters } from './xmlCharacters.js'

/**
 * Rich text naar de drie vormen van het koppelvlak: platte tekst, HTML en de structuur zelf, plus
 * de figuren (R-14, R-50, R-202, `koppelvlak.md` hoofdstuk 3).
 *
 * **Met de hand en niet met `renderHTML` van de editor.** De editor tekent een figuur als kader
 * zonder afbeelding en een bronvermelding als `data-referentie`; het koppelvlak schrijft iets
 * anders voor, met een `<img>`, de vermelding zoals de lezer haar ziet en LaTeX als tekst. En dit
 * pakket hangt alleen van Zod af. De tabel in het koppelvlak is daarom hier uitgeschreven, knoop
 * voor knoop.
 *
 * **Een onbekende knoop of mark is een fout, geen weglating.** Het koppelvlak belooft dat er geen
 * inhoud stil verdwijnt. Komt er een extensie in de editor bij, dan breekt de bewaking in
 * `packages/renderer` (`richTextExport.node.test.ts`) en daarna deze functie, en niet het bericht
 * zonder dat iemand het merkt.
 */

/** De knopen die het koppelvlak kent; elke knoop van het editorschema staat hier of in geen lijst. */
export const EXPORTED_NODES = [
  'doc',
  'paragraph',
  'text',
  'heading',
  'blockquote',
  'bulletList',
  'orderedList',
  'listItem',
  'taskList',
  'taskItem',
  'codeBlock',
  'horizontalRule',
  'hardBreak',
  'table',
  'tableRow',
  'tableHeader',
  'tableCell',
  'blockMath',
  'inlineMath',
  'figuur',
  'referentie',
] as const

/** De marks die het koppelvlak kent. */
export const EXPORTED_MARKS = [
  'bold',
  'italic',
  'underline',
  'strike',
  'code',
  'subscript',
  'superscript',
  'highlight',
  'link',
] as const

/**
 * Wat er bewust niet meegaat: wie welk stuk schreef (`ychange`) en het anker van een opmerking
 * tussen medeschrijvers (`opmerking`). Dat zijn gegevens van de samenwerking binnen dit product en
 * geen deel van het antwoord, en een opmerkingdraad kan intern zijn.
 */
export const EXCLUDED_MARKS = ['ychange', 'opmerking'] as const
const EXCLUDED_ATTRIBUTES = ['ychange'] as const

const NODES: ReadonlySet<string> = new Set(EXPORTED_NODES)
const MARKS: ReadonlySet<string> = new Set(EXPORTED_MARKS)
const EXCLUDED: ReadonlySet<string> = new Set<string>(EXCLUDED_MARKS)

export interface RichTextContext {
  /** De vermelding zoals de lezer haar ziet, per `citatieId`, bijvoorbeeld `(Voorbeeld, 2026)`. */
  readonly citations: Readonly<Record<string, string>>
  /** Het adres van een bestand in de opslag, uit zijn hash. */
  readonly fileUrl: (hash: string) => string
}

export interface RichTextExport {
  readonly text: string
  readonly html: string
  readonly figures: readonly (DocumentVerwijzing & { readonly url: string })[]
  readonly structure: ProseMirrorNode
}

export function exportRichText(root: ProseMirrorNode, context: RichTextContext): RichTextExport {
  check(root)
  const figures: (DocumentVerwijzing & { url: string })[] = []
  collectFigures(root, context, figures)
  return {
    text: blocksToText(root.content ?? [], context).join('\n'),
    html: (root.content ?? []).map((node) => nodeToHtml(node, context)).join(''),
    figures,
    structure: withoutCollaboration(root),
  }
}

function check(node: ProseMirrorNode): void {
  if (!NODES.has(node.type)) throw new Error(`rich text: de knoop ${node.type} heeft geen vorm in het koppelvlak`)
  for (const mark of node.marks ?? []) {
    if (!MARKS.has(mark.type) && !EXCLUDED.has(mark.type)) {
      throw new Error(`rich text: de mark ${mark.type} heeft geen vorm in het koppelvlak`)
    }
  }
  for (const child of node.content ?? []) check(child)
}

/** De structuur zonder de gegevens van de samenwerking, met de volgorde van de sleutels intact. */
function withoutCollaboration(node: ProseMirrorNode): ProseMirrorNode {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(node)) {
    if (key === 'attrs' && value !== undefined) {
      const attrs = Object.fromEntries(
        Object.entries(value as Record<string, unknown>).filter(
          ([name]) => !(EXCLUDED_ATTRIBUTES as readonly string[]).includes(name),
        ),
      )
      if (Object.keys(attrs).length > 0) out['attrs'] = attrs
    } else if (key === 'marks' && value !== undefined) {
      const marks = (value as ProseMirrorNode['marks'] & object).filter((mark) => !EXCLUDED.has(mark.type))
      if (marks.length > 0) out['marks'] = marks
    } else if (key === 'content' && value !== undefined) {
      out['content'] = (value as ProseMirrorNode[]).map(withoutCollaboration)
    } else if (value !== undefined) {
      out[key] = value
    }
  }
  return out as unknown as ProseMirrorNode
}

function attribute(node: ProseMirrorNode, name: string): unknown {
  return node.attrs?.[name]
}

function stringAttribute(node: ProseMirrorNode, name: string): string {
  const value = attribute(node, name)
  if (typeof value !== 'string') throw new Error(`rich text: ${node.type} mist ${name}`)
  return value
}

/**
 * De vermelding zoals de lezer haar zag, of, als de pod haar niet meegaf, de bronnen tussen haken.
 *
 * Een pod van vóór het meegeven van de bronnen (opdracht 1b) stuurt geen vermeldingen, en pods
 * worden nooit bijgewerkt. Een fout hier zou het indienen van elk zo'n formulier met een
 * bronvermelding blokkeren, want het bericht wordt in dezelfde transactie als de revisie gebouwd.
 * De bronnen tussen haken zeggen wat er stond, en `vermeldingOntbreekt` zegt dat het geen
 * vermelding is (`koppelvlak.md` hoofdstuk 3).
 */
function citationOf(node: ProseMirrorNode, context: RichTextContext): { readonly text: string; readonly missing: boolean } {
  const text = context.citations[stringAttribute(node, 'citatieId')]
  if (text !== undefined) return { text, missing: false }
  return { text: `[${sourcesOf(node).join(', ')}]`, missing: true }
}

function sourcesOf(node: ProseMirrorNode): string[] {
  const sources = attribute(node, 'bronnen')
  return Array.isArray(sources) ? sources.filter((s): s is string => typeof s === 'string') : []
}

function figureOf(node: ProseMirrorNode): DocumentVerwijzing {
  const grootteBytes = attribute(node, 'grootteBytes')
  if (typeof grootteBytes !== 'number') throw new Error('rich text: figuur mist grootteBytes')
  return { hash: stringAttribute(node, 'hash'), bestandsnaam: stringAttribute(node, 'bestandsnaam'), grootteBytes }
}

function collectFigures(
  node: ProseMirrorNode,
  context: RichTextContext,
  out: (DocumentVerwijzing & { url: string })[],
): void {
  if (node.type === 'figuur') {
    const figure = figureOf(node)
    out.push({ ...figure, url: context.fileUrl(figure.hash) })
  }
  for (const child of node.content ?? []) collectFigures(child, context, out)
}

// Platte tekst: een regel per alinea, kop, lijstitem, tabelrij, formuleblok of figuur.

function blocksToText(nodes: readonly ProseMirrorNode[], context: RichTextContext): string[] {
  return nodes.flatMap((node) => blockToText(node, context))
}

function blockToText(node: ProseMirrorNode, context: RichTextContext): string[] {
  switch (node.type) {
    case 'paragraph':
    case 'heading':
    case 'codeBlock':
      return [inlineToText(node.content ?? [], context)]
    case 'blockquote':
    case 'bulletList':
    case 'orderedList':
    case 'listItem':
    case 'taskList':
    case 'taskItem':
    case 'table':
      return blocksToText(node.content ?? [], context)
    case 'tableRow':
      return [(node.content ?? []).map((cell) => blocksToText(cell.content ?? [], context).join(' ')).join('\t')]
    case 'blockMath':
      return [stringAttribute(node, 'latex')]
    case 'figuur':
      return [`[${figureOf(node).bestandsnaam}]`]
    case 'horizontalRule':
      return []
    default:
      throw new Error(`rich text: de knoop ${node.type} hoort niet op blokniveau`)
  }
}

function inlineToText(nodes: readonly ProseMirrorNode[], context: RichTextContext): string {
  return nodes
    .map((node) => {
      switch (node.type) {
        case 'text':
          return node.text ?? ''
        case 'hardBreak':
          return '\n'
        case 'inlineMath':
          return stringAttribute(node, 'latex')
        case 'referentie':
          return citationOf(node, context).text
        default:
          throw new Error(`rich text: de knoop ${node.type} hoort niet in een regel`)
      }
    })
    .join('')
}

// HTML: de tabel uit het koppelvlak.

/**
 * Tekst voor XML en HTML: de drie tekens die opmaak zijn, en regeleinden als tekenverwijzing.
 *
 * Een regeleinde als `&#10;` en niet letterlijk, zodat elk element op één regel staat en het
 * inspringen van het bericht nooit in een waarde terechtkomt. In XML en in HTML betekent `&#10;`
 * precies een regeleinde, dus de inhoud is dezelfde.
 */
export function escapeText(value: string): string {
  // Eerst de tekens die XML 1.0 niet toelaat eruit (`xmlCharacters.ts`): als tekenverwijzing zouden
  // ze het document net zo goed breken. In HTML zijn het stuurtekens zonder betekenis.
  return withoutForbiddenXmlCharacters(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r/g, '&#13;')
    .replace(/\n/g, '&#10;')
}

export function escapeAttribute(value: string): string {
  return escapeText(value).replace(/"/g, '&quot;')
}

const SAFE_LINK = /^(https?:|mailto:)/i

function alignment(node: ProseMirrorNode): string {
  const align = attribute(node, 'textAlign')
  return typeof align === 'string' && align !== 'left' ? ` style="text-align: ${escapeAttribute(align)}"` : ''
}

function span(node: ProseMirrorNode, name: string): string {
  const value = attribute(node, name)
  return typeof value === 'number' && value > 1 ? ` ${name}="${value}"` : ''
}

function children(node: ProseMirrorNode, context: RichTextContext): string {
  return (node.content ?? []).map((child) => nodeToHtml(child, context)).join('')
}

function nodeToHtml(node: ProseMirrorNode, context: RichTextContext): string {
  switch (node.type) {
    case 'text':
      return withMarks(escapeText(node.text ?? ''), node.marks ?? [])
    case 'paragraph':
      return `<p${alignment(node)}>${children(node, context)}</p>`
    case 'heading': {
      const level = attribute(node, 'level')
      if (level !== 1 && level !== 2 && level !== 3) throw new Error(`rich text: kop van niveau ${String(level)}`)
      return `<h${level}${alignment(node)}>${children(node, context)}</h${level}>`
    }
    case 'blockquote':
      return `<blockquote>${children(node, context)}</blockquote>`
    case 'bulletList':
      return `<ul>${children(node, context)}</ul>`
    case 'orderedList': {
      const start = attribute(node, 'start')
      return `<ol${typeof start === 'number' && start !== 1 ? ` start="${start}"` : ''}>${children(node, context)}</ol>`
    }
    case 'listItem':
      return `<li>${children(node, context)}</li>`
    case 'taskList':
      return `<ul data-type="taskList">${children(node, context)}</ul>`
    case 'taskItem':
      return `<li data-checked="${attribute(node, 'checked') === true ? 'true' : 'false'}">${children(node, context)}</li>`
    case 'codeBlock':
      return `<pre><code>${children(node, context)}</code></pre>`
    case 'horizontalRule':
      return '<hr>'
    case 'hardBreak':
      return '<br>'
    case 'table':
      return `<table>${children(node, context)}</table>`
    case 'tableRow':
      return `<tr>${children(node, context)}</tr>`
    case 'tableHeader':
      return `<th${span(node, 'colspan')}${span(node, 'rowspan')}>${children(node, context)}</th>`
    case 'tableCell':
      return `<td${span(node, 'colspan')}${span(node, 'rowspan')}>${children(node, context)}</td>`
    case 'blockMath': {
      const latex = escapeAttribute(stringAttribute(node, 'latex'))
      return `<div class="ef-formule" data-latex="${latex}">${latex}</div>`
    }
    case 'inlineMath': {
      const latex = escapeAttribute(stringAttribute(node, 'latex'))
      return `<span class="ef-formule" data-latex="${latex}">${latex}</span>`
    }
    case 'figuur': {
      const figure = figureOf(node)
      const name = escapeAttribute(figure.bestandsnaam)
      return (
        `<figure class="ef-figuur" data-hash="${escapeAttribute(figure.hash)}">` +
        `<img src="${escapeAttribute(context.fileUrl(figure.hash))}" alt="${name}">` +
        `<figcaption>${escapeText(figure.bestandsnaam)}</figcaption></figure>`
      )
    }
    case 'referentie': {
      const citation = citationOf(node, context)
      return (
        `<span class="ef-citatie" data-citatie="${escapeAttribute(stringAttribute(node, 'citatieId'))}" ` +
        `data-bronnen="${escapeAttribute(sourcesOf(node).join(' '))}"` +
        `${citation.missing ? ' data-vermelding="ontbreekt"' : ''}>${escapeText(citation.text)}</span>`
      )
    }
    default:
      throw new Error(`rich text: de knoop ${node.type} heeft geen vorm in het koppelvlak`)
  }
}

const MARK_TAG: Readonly<Record<string, string>> = {
  bold: 'strong',
  italic: 'em',
  underline: 'u',
  strike: 's',
  code: 'code',
  subscript: 'sub',
  superscript: 'sup',
  highlight: 'mark',
}

function withMarks(html: string, marks: NonNullable<ProseMirrorNode['marks']>): string {
  let out = html
  // Van binnen naar buiten, zodat de eerste mark de buitenste tag is.
  for (const mark of [...marks].reverse()) {
    if (EXCLUDED.has(mark.type)) continue
    if (mark.type === 'link') {
      const href = mark.attrs?.['href']
      // Een adres buiten de toegestane schema's verliest de link en houdt de tekst.
      if (typeof href === 'string' && SAFE_LINK.test(href)) out = `<a href="${escapeAttribute(href)}">${out}</a>`
      continue
    }
    const tag = MARK_TAG[mark.type]
    if (tag === undefined) throw new Error(`rich text: de mark ${mark.type} heeft geen vorm in het koppelvlak`)
    out = `<${tag}>${out}</${tag}>`
  }
  return out
}
